import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { PublicNavbar, PublicFooter } from '../components/PublicLayout';
import {
  X, Check, Minus, ChevronDown, Globe, MapPin, Award, Briefcase,
  BarChart2, Users, Star, ExternalLink, ArrowLeft
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPARE_LIMIT = 3;

const Row = ({ label, values, render }) => (
  <tr className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
    <td className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50/80 w-36 shrink-0">{label}</td>
    {values.map((val, i) => (
      <td key={i} className="py-3 px-5 text-sm text-gray-700 text-center">
        {render ? render(val, i) : (val || <Minus className="h-4 w-4 text-gray-300 mx-auto" />)}
      </td>
    ))}
    {/* Empty cells for missing contractors */}
    {Array.from({ length: COMPARE_LIMIT - values.length }).map((_, i) => (
      <td key={`empty-${i}`} className="py-3 px-5" />
    ))}
  </tr>
);

const ScoreBar = ({ score }) => (
  <div className="flex items-center gap-2 justify-center">
    <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full ${score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-amber-400' : 'bg-gray-300'}`}
        style={{ width: `${score}%` }}
      />
    </div>
    <span className="text-xs font-bold text-gray-700">{score}%</span>
  </div>
);

export default function ContractorCompare() {
  const [searchParams] = useSearchParams();
  const slugs = searchParams.get('slugs')?.split(',').filter(Boolean).slice(0, COMPARE_LIMIT) || [];
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      if (!slugs.length) { setLoading(false); return; }
      setLoading(true);
      const results = await Promise.all(
        slugs.map(slug =>
          axios.get(`${API}/public/contractor/${slug}`)
            .then(r => ({ ...r.data.profile, name: r.data.org?.display_name || r.data.profile?.name, org: r.data.org }))
            .catch(() => null)
        )
      );
      setContractors(results.filter(Boolean));
      setLoading(false);
    };
    fetchAll();
  }, [searchParams.get('slugs')]); // eslint-disable-line

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Helmet><title>Compare Contractors | ExpoStand</title></Helmet>
        <PublicNavbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500 text-sm">Loading contractor profiles...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!contractors.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Helmet><title>Compare Contractors | ExpoStand</title></Helmet>
        <PublicNavbar />
        <div className="flex-1 flex items-center justify-center text-center px-4">
          <div>
            <BarChart2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-700 mb-2">No contractors to compare</h2>
            <p className="text-gray-500 text-sm mb-5">Go to the search page and select contractors to compare</p>
            <Link to="/find-contractors" className="bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-blue-700">
              Find Contractors
            </Link>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  const names = contractors.map(c => c.name || c.org?.display_name || 'Contractor');
  const slugList = slugs;

  const pageTitle = loading ? 'Compare Contractors | ExpoStand'
    : contractors.length === 0 ? 'Compare Contractors | ExpoStand'
    : `Compare ${names.join(' vs ')} | ExpoStand`;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={contractors.length > 0 ? `Side-by-side comparison of ${names.join(', ')} exhibition stand contractors on ExpoStand.` : 'Compare exhibition stand contractors side by side on ExpoStand.'} />
        <meta property="og:title" content={pageTitle} />
      </Helmet>

      <PublicNavbar />

      {/* Header */}
      <div className="bg-gray-900 text-white py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <Link to="/find-contractors" className="text-xs text-gray-400 hover:text-white flex items-center gap-1 mb-4">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Search
          </Link>
          <h1 className="text-2xl font-bold mb-1">Contractor Comparison</h1>
          <p className="text-gray-400 text-sm">Side-by-side comparison of {contractors.length} contractors</p>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="flex-1 py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                {/* Header Row */}
                <thead>
                  <tr className="bg-gray-900 text-white">
                    <th className="py-4 px-4 text-left text-xs font-semibold w-36" />
                    {contractors.map((c, i) => {
                      const name = c.name || c.org?.display_name || 'Contractor';
                      const slug = slugList[i];
                      return (
                        <th key={i} className="py-4 px-5 text-center">
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center font-bold text-white text-lg shadow-sm overflow-hidden">
                              {c.logo_url ? <img src={c.logo_url} alt={name} className="w-full h-full object-cover" /> : name.charAt(0)}
                            </div>
                            <div>
                              <p className="font-bold text-sm text-white">{name}</p>
                              <p className="text-xs text-gray-400">{c.org?.country || '—'}</p>
                            </div>
                            <Link to={`/contractor/${slug}`}
                              className="text-[10px] text-blue-300 hover:text-white flex items-center gap-0.5">
                              View Profile <ExternalLink className="h-2.5 w-2.5" />
                            </Link>
                          </div>
                        </th>
                      );
                    })}
                    {Array.from({ length: COMPARE_LIMIT - contractors.length }).map((_, i) => (
                      <th key={`empty-h-${i}`} className="py-4 px-5">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-12 h-12 rounded-xl border-2 border-dashed border-gray-700 flex items-center justify-center">
                            <span className="text-2xl text-gray-600">+</span>
                          </div>
                          <Link to="/find-contractors" className="text-[10px] text-gray-500 hover:text-white">Add contractor</Link>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {/* Profile Score */}
                  <Row label="Profile Score" values={contractors}
                    render={(c) => <ScoreBar score={c.profile_score || 0} />} />

                  {/* Location */}
                  <Row label="Location" values={contractors}
                    render={(c) => (
                      <span className="flex items-center gap-1 justify-center text-xs">
                        <MapPin className="h-3 w-3 text-gray-400" />
                        {c.org?.city && c.org?.country ? `${c.org.city}, ${c.org.country}` : c.org?.country || '—'}
                      </span>
                    )} />

                  {/* Countries Covered */}
                  <Row label="Countries" values={contractors}
                    render={(c) => {
                      const count = c.coverage?.countries?.length || 0;
                      return <span className="font-bold text-blue-600">{count} <span className="text-xs text-gray-500 font-normal">countries</span></span>;
                    }} />

                  {/* Services */}
                  <Row label="Services" values={contractors}
                    render={(c) => {
                      const services = (c.services || []).map(s => s.name || s).filter(Boolean);
                      return (
                        <div className="flex flex-wrap gap-1 justify-center">
                          {services.slice(0, 3).map((s, i) => (
                            <span key={i} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full">{s}</span>
                          ))}
                          {services.length > 3 && <span className="text-[10px] text-gray-400">+{services.length - 3}</span>}
                        </div>
                      );
                    }} />

                  {/* Active Shows */}
                  <Row label="Active Shows" values={contractors}
                    render={(c) => {
                      const shows = c.coverage?.active_shows || [];
                      return shows.length > 0 ? (
                        <div className="flex flex-wrap gap-1 justify-center">
                          {shows.slice(0, 2).map((s, i) => (
                            <span key={i} className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full">⭐ {s}</span>
                          ))}
                          {shows.length > 2 && <span className="text-[10px] text-gray-400">+{shows.length - 2}</span>}
                        </div>
                      ) : <Minus className="h-4 w-4 text-gray-300 mx-auto" />;
                    }} />

                  {/* Proposals Sent */}
                  <Row label="Proposals" values={contractors}
                    render={(c) => <span className="font-bold text-gray-800">{c.stats?.total_sent || 0}</span>} />

                  {/* Win Rate */}
                  <Row label="Win Rate" values={contractors}
                    render={(c) => {
                      const rate = c.stats?.win_rate || 0;
                      return (
                        <span className={`font-bold ${rate >= 50 ? 'text-emerald-600' : rate >= 25 ? 'text-amber-600' : 'text-gray-500'}`}>
                          {rate}%
                        </span>
                      );
                    }} />

                  {/* Plan */}
                  <Row label="Plan" values={contractors}
                    render={(c) => {
                      const plan = c.org?.plan_code || 'free';
                      const colors = { pro: 'bg-blue-100 text-blue-700', enterprise: 'bg-purple-100 text-purple-700', free: 'bg-gray-100 text-gray-600' };
                      return <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${colors[plan] || colors.free}`}>{plan}</span>;
                    }} />

                  {/* Tagline */}
                  <Row label="About" values={contractors}
                    render={(c) => <span className="text-xs text-gray-500 italic line-clamp-2">{c.tagline || '—'}</span>} />

                  {/* CTA Row */}
                  <tr className="bg-gray-50">
                    <td className="py-4 px-4" />
                    {contractors.map((c, i) => (
                      <td key={i} className="py-4 px-5 text-center">
                        <Link to={`/contractor/${slugList[i]}`}
                          className="inline-block bg-blue-600 text-white text-xs font-bold px-4 py-2 rounded-xl hover:bg-blue-700 transition-colors">
                          View Full Profile
                        </Link>
                      </td>
                    ))}
                    {Array.from({ length: COMPARE_LIMIT - contractors.length }).map((_, i) => (
                      <td key={`cta-empty-${i}`} className="py-4 px-5" />
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Back link */}
          <div className="mt-6 text-center">
            <Link to="/find-contractors" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 justify-center">
              <ArrowLeft className="h-4 w-4" /> Edit comparison — go back to search
            </Link>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
